import { BaseService } from './baseService';

export interface User {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  role: 'admin' | 'manager' | 'analyst' | 'viewer' | 'client';
  department?: string;
  clientId?: string;
  isActive: boolean;
  createdAt?: string;
  updatedAt?: string;
  lastLoginAt?: string;
}

export interface UserPreferences {
  theme: 'light' | 'dark' | 'auto';
  notifications: {
    email: boolean;
    push: boolean;
  };
}

export interface CreateUserRequest {
  email: string;
  firstName: string;
  lastName: string;
  role: 'admin' | 'manager' | 'analyst' | 'viewer' | 'client';
  department?: string;
  clientId?: string;
  isActive?: boolean;
}

export interface UpdateUserRequest {
  email?: string;
  firstName?: string;
  lastName?: string;
  role?: 'admin' | 'manager' | 'analyst' | 'viewer' | 'client';
  department?: string;
  clientId?: string;
  isActive?: boolean;
}

export interface ListUsersParams {
  page?: number;
  limit?: number;
  role?: string;
  isActive?: boolean;
  search?: string;
}

export interface ListUsersResponse {
  users: User[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

export class UserService extends BaseService {
  /**
   * Get user preferences
   */
  async getUserPreferences(userId: string): Promise<UserPreferences> {
    const response = await this.api.get(`/users/${userId}/preferences`);
    return response.data.data;
  }

  /**
   * Update user preferences
   */
  async updateUserPreferences(userId: string, preferences: UserPreferences): Promise<UserPreferences> {
    const response = await this.api.put(`/users/${userId}/preferences`, preferences);
    return response.data.data;
  }

  /**
   * List all users (Admin and Manager only)
   */
  async listUsers(params?: ListUsersParams): Promise<ListUsersResponse> {
    const queryParams = new URLSearchParams();
    
    if (params?.page) queryParams.set('page', params.page.toString());
    if (params?.limit) queryParams.set('limit', params.limit.toString());
    if (params?.role) queryParams.set('role', params.role);
    if (params?.isActive !== undefined) queryParams.set('isActive', params.isActive.toString());
    if (params?.search) queryParams.set('search', params.search);

    const response = await this.api.get(`/users?${queryParams.toString()}`);
    return response.data.data;
  }

  /**
   * Get user by ID (Admin and Manager only)
   */
  async getUser(userId: string): Promise<User> {
    const response = await this.api.get(`/users/${userId}`);
    return response.data.data;
  }

  /**
   * Create a new user (Admin and Manager only)
   */
  async createUser(userData: CreateUserRequest): Promise<User> {
    const response = await this.api.post('/users', userData);
    return response.data.data;
  }

  /**
   * Update user (Admin and Manager only)
   */
  async updateUser(userId: string, userData: UpdateUserRequest): Promise<User> {
    const response = await this.api.put(`/users/${userId}`, userData);
    return response.data.data;
  }

  /**
   * Delete user (Admin only)
   */
  async deleteUser(userId: string): Promise<void> {
    await this.api.delete(`/users/${userId}`);
  }
}

export const userService = new UserService();